import type { CSSProperties, ReactNode, RefCallback, RefObject } from "react";

import { DROPDOWN_PANEL_SHELL_CLASS } from "@/components/molecules/dropdownPanel/dropdownPanel";

export interface FloatingSuggestionPanelProps {
  /** Returns the trigger's rect for positioning; panel is hidden when null. */
  clientRect: (() => DOMRect | null) | null;
  children: ReactNode;
  /** Optional inline style overrides for the wrapper (e.g. minWidth, maxWidth). */
  style?: CSSProperties;
  /** Root element of the panel (scopes queries for floating choice fields). */
  panelRef?:
    | RefObject<HTMLDivElement | null>
    | RefCallback<HTMLDivElement | null>;
}

const setPanelRef = (
  panelRef: FloatingSuggestionPanelProps["panelRef"],
  node: HTMLDivElement | null,
) => {
  if (panelRef == null) return;
  if (typeof panelRef === "function") {
    panelRef(node);
  } else {
    panelRef.current = node;
  }
};

/**
 * Positions suggestion panel content below a trigger (e.g. @ or {).
 * Used by both directive builder (@) and variable suggestion ({) flows.
 */
export const FloatingSuggestionPanel = ({
  clientRect,
  children,
  style: styleOverride,
  panelRef,
}: FloatingSuggestionPanelProps): ReactNode => {
  const rect = clientRect?.() ?? null;
  if (rect == null) return null;

  const style: CSSProperties = {
    position: "fixed",
    left: rect.left,
    top: rect.bottom + 4,
    width: "max-content",
    maxWidth: 320,
    ...styleOverride,
  };

  return (
    <div
      ref={(node) => setPanelRef(panelRef, node)}
      data-dropdown-panel=""
      className={DROPDOWN_PANEL_SHELL_CLASS}
      style={style}
    >
      {children}
    </div>
  );
};
